const ROOT_BOTTOM_EXTENSION = "100000px";

class StickyAtc extends HTMLElement {
  constructor() {
    super();

    this.visible = false;
  }

  connectedCallback() {
    this.form = document.getElementById(this.getAttribute("form-id"));
    if (!this.form) return;

    this.productForm = this.form.closest("product-form");
    this.mainAtc = this.form.querySelector("[data-product-form-atc]");
    this.mainPrice = this.form.querySelector("[data-main-product-price]");

    this.select = this.querySelector("[data-sticky-atc-select]");
    this.sizeName = this.querySelector("[data-sticky-atc-size-name]");
    this.sizeNote = this.querySelector("[data-sticky-atc-size-note]");
    this.price = this.querySelector("[data-sticky-atc-price]");
    this.button = this.querySelector("[data-sticky-atc-button]");
    this.image = this.querySelector(".sticky-atc__img");
    this.error = this.querySelector("[data-sticky-atc-error]");

    this.onVariantChanged = (e) => {
      this._updateImage(e.detail?.variant);
      queueMicrotask(() => this._sync());
    };
    this.productForm?.addEventListener(
      "variant:changed",
      this.onVariantChanged,
    );

    this.onSelectChange = () => this._selectSize(this.select.value);
    this.select?.addEventListener("change", this.onSelectChange);

    this._sync();
    this._mirrorErrors();

    this.header = document.querySelector("sticky-header");
    if (this.header) {
      this.headerObserver = new ResizeObserver(() => this._observe());
      this.headerObserver.observe(this.header);
    } else {
      this._observe();
    }
  }

  disconnectedCallback() {
    this.productForm?.removeEventListener(
      "variant:changed",
      this.onVariantChanged,
    );
    this.select?.removeEventListener("change", this.onSelectChange);
    this.headerObserver?.disconnect();
    this.observer?.disconnect();
    this.errorObserver?.disconnect();
    clearTimeout(this.errorTimeout);
  }

  get sizeInputs() {
    const position = this.getAttribute("size-option-position");
    if (!position) return [];

    return [...this.form.querySelectorAll(`input[name="option-${position}"]`)];
  }

  _observe() {
    if (!this.mainAtc) return;

    this.observer?.disconnect();

    const headerHeight = this.header?.offsetHeight || 0;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        const rootTop = entry.rootBounds?.top ?? headerHeight;
        const scrolledPast =
          !entry.isIntersecting && entry.boundingClientRect.bottom <= rootTop;

        this._toggle(scrolledPast);
      },
      {
        root: document,
        rootMargin: `-${Math.round(headerHeight)}px 0px ${ROOT_BOTTOM_EXTENSION} 0px`,
      },
    );

    this.observer.observe(this.mainAtc);
  }

  _toggle(show) {
    if (show === this.visible) return;

    this.visible = show;
    this.classList.toggle("is-visible", show);
    this.toggleAttribute("inert", !show);
  }

  _selectSize(optionValueId) {
    const input = this.sizeInputs.find(
      (input) => input.value === optionValueId,
    );
    if (!input || input.checked) return;

    input.checked = true;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  _sync() {
    this._syncSizes();

    if (this.price && this.mainPrice) {
      this.price.innerHTML = this.mainPrice.innerHTML;
    }

    if (this.button && this.mainAtc) {
      this.button.disabled = this.mainAtc.disabled;
    }
  }

  _syncSizes() {
    const inputs = this.sizeInputs;
    if (!this.select || !inputs.length) return;

    const soldOutLabel = this.getAttribute("sold-out-label");

    this.select.replaceChildren(
      ...inputs.map((input) => {
        const variant = this._variantForSize(parseInt(input.value, 10));

        const option = document.createElement("option");
        option.value = input.value;
        option.textContent = [
          input.dataset.optionValue,
          this._visibleNote(input.closest("label")),
          variant && !variant.available ? soldOutLabel : "",
        ]
          .filter(Boolean)
          .join(" - ");
        option.disabled = !variant;
        option.selected = input.checked;

        return option;
      }),
    );

    const checked = inputs.find((input) => input.checked);
    if (!checked) return;

    this.sizeName.textContent = checked.dataset.optionValue;
    this.sizeNote.textContent = this._visibleNote(checked.closest("label"));
  }

  _variantForSize(sizeValueId) {
    const {
      variants = [],
      optionsByVariant = [],
      productOptionsCount,
    } = window.scrapeSectionData(this.form);
    const sizeIndex =
      parseInt(this.getAttribute("size-option-position"), 10) - 1;

    const selectedIds = new Array(productOptionsCount)
      .fill(null)
      .map((_, i) =>
        i === sizeIndex
          ? sizeValueId
          : parseInt(this.form[`option-${i + 1}`]?.value, 10),
      );

    const match = optionsByVariant.find(({ variantOptions }) =>
      selectedIds.every((id, i) => variantOptions[i].id === id),
    );

    return match && variants.find(({ id }) => id === match.variantId);
  }

  _visibleNote(label) {
    const notes = label?.querySelectorAll("[data-size-note]") || [];
    const note = [...notes].find((note) => note.style.display !== "none");

    return note?.textContent.trim() || "";
  }

  _updateImage(variant) {
    const src = variant?.featured_image?.src;
    if (!this.image || !src) return;

    const url = new URL(src, window.location.origin);
    url.searchParams.set("width", 160);

    this.image.removeAttribute("srcset");
    this.image.src = url.toString();
  }

  _mirrorErrors() {
    if (!this.error) return;

    this.errorObserver = new MutationObserver((mutations) => {
      if (!this.visible) return;

      const error = mutations
        .flatMap((mutation) => [...mutation.addedNodes])
        .find((node) => node.classList?.contains("add-to-cart-error"));
      if (!error) return;

      this.error.textContent = error.textContent;
      this.error.hidden = false;

      clearTimeout(this.errorTimeout);
      this.errorTimeout = setTimeout(() => {
        this.error.hidden = true;
      }, 3000);
    });

    this.errorObserver.observe(this.form, { childList: true });
  }
}

if (!customElements.get("sticky-atc")) {
  customElements.define("sticky-atc", StickyAtc);
}
